/**
 * useDebounce is a custom React hook that delays updating the value
 * until after a specified delay period has elapsed since the last
 * change. It is commonly used to reduce the frequency of expensive
 * operations, such as API calls or search queries, by waiting until
 * the user has stopped typing or interacting for a certain period.
 *
 * This hook is useful in scenarios where you want to avoid triggering
 * an action immediately upon each change and instead want to wait
 * until the user has finished making changes. For example, in a
 * search input field, you might use this hook to delay the search
 * request until the user has stopped typing for a brief moment.
 *
 * Parameters:
 * - value: The value to debounce, usually the input or search query.
 * - delay: The debounce delay in milliseconds; the amount of time to
 *   wait after the last change before updating the debounced value.
 *
 * Returns:
 * - debouncedValue: The value after applying the debounce delay.
 *
 * Example usage:
 * const searchQuery = useDebounce(query, 300);
 * This would ensure that the searchQuery is only updated 300ms after
 * the user has stopped typing, reducing the number of search requests
 * sent while the user is still typing.
 */
import { useState, useEffect } from "react";

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

export default useDebounce;